class AfroQueenCatalogueBrowser extends HTMLElement {
  connectedCallback() {
    this.source = this.dataset.source;
    this.locale = this.dataset.locale || 'pt-PT';
    const urlCategory = new URL(window.location.href).searchParams.get('aq-category') || '';
    this.scopeCollection = urlCategory || this.dataset.collection || '';
    this.initialQuery = this.dataset.query || '';
    this.pageSize = Number(this.dataset.pageSize || 24);
    this.visibleCount = this.pageSize;
    this.products = [];
    this.filtered = [];
    this.money = new Intl.NumberFormat(this.locale, { style: 'currency', currency: 'EUR' });
    this.bindEvents();
    this.load();
  }

  bindEvents() {
    this.addEventListener('input', (event) => {
      if (event.target.matches('[data-aq-search]')) {
        this.visibleCount = this.pageSize;
        this.apply();
      }
    });
    this.addEventListener('change', (event) => {
      if (event.target.matches('[data-aq-filter], [data-aq-sort]')) {
        this.visibleCount = this.pageSize;
        this.apply();
      }
    });
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-aq-more]')) {
        this.visibleCount += this.pageSize;
        this.render();
        return;
      }
      if (event.target.closest('[data-aq-reset]')) {
        const search = this.querySelector('[data-aq-search]');
        if (search) search.value = '';
        this.querySelectorAll('[data-aq-filter]').forEach((select) => { select.value = ''; });
        const sort = this.querySelector('[data-aq-sort]');
        if (sort) sort.value = 'az';
        this.visibleCount = this.pageSize;
        this.apply();
        return;
      }
      const details = event.target.closest('[data-aq-details]');
      if (details) this.openDetails(details.dataset.aqDetails);
    });
  }

  async load() {
    try {
      const response = await fetch(this.source, { credentials: 'same-origin' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const payload = await response.json();
      this.products = Array.isArray(payload.products) ? payload.products : [];
      this.populateFilters();
      const search = this.querySelector('[data-aq-search]');
      if (search && this.initialQuery) search.value = this.initialQuery;
      this.apply();
      this.dataset.loaded = 'true';
    } catch (error) {
      const status = this.querySelector('[data-aq-status]');
      if (status) status.textContent = this.copy('loadError');
      this.classList.add('aq-catalogue-browser--error');
      console.error('[AfroQueen catalogue]', error);
    }
  }

  copy(key) {
    const pt = {
      loadError: 'Não foi possível carregar o catálogo neste momento.',
      noResults: 'Não encontrámos produtos com estes filtros.',
      result: 'produto', results: 'produtos', pricePending: 'Preço em breve',
      availability: 'Disponibilidade a confirmar', from: 'Desde', details: 'Ver detalhes',
      moreVariants: 'mais opções', imagePending: 'Imagem do produto'
    };
    const en = {
      loadError: 'The catalogue could not be loaded right now.',
      noResults: 'No products match these filters.',
      result: 'product', results: 'products', pricePending: 'Price coming soon',
      availability: 'Availability to be confirmed', from: 'From', details: 'View details',
      moreVariants: 'more options', imagePending: 'Product image'
    };
    return (this.locale.toLowerCase().startsWith('pt') ? pt : en)[key];
  }

  normalize(value) {
    return String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
  }

  unique(field) {
    return [...new Set(this.products.map((product) => product[field]).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b, this.locale, { sensitivity: 'base' }));
  }

  populateFilters() {
    for (const [field, selector] of [['vendor','[data-aq-vendor]'],['productType','[data-aq-type]'],['collection','[data-aq-collection]']]) {
      const select = this.querySelector(selector);
      if (!select) continue;
      for (const value of this.unique(field)) {
        const option = document.createElement('option');
        option.value = value;
        option.textContent = value;
        select.append(option);
      }
    }
    const categoryField = this.querySelector('[data-aq-collection-field]');
    if (categoryField && this.scopeCollection && this.scopeCollection !== 'all') categoryField.hidden = true;
  }

  apply() {
    const query = this.normalize(this.querySelector('[data-aq-search]')?.value);
    const vendor = this.querySelector('[data-aq-vendor]')?.value || '';
    const type = this.querySelector('[data-aq-type]')?.value || '';
    const collection = this.querySelector('[data-aq-collection]')?.value || '';
    const lockedCollection = this.scopeCollection && this.scopeCollection !== 'all' ? this.scopeCollection : '';

    this.filtered = this.products.filter((product) => {
      if (lockedCollection && product.collectionHandle !== lockedCollection) return false;
      if (vendor && product.vendor !== vendor) return false;
      if (type && product.productType !== type) return false;
      if (collection && product.collection !== collection) return false;
      if (!query) return true;
      const text = this.normalize([
        product.title, product.vendor, product.productType, product.collection,
        ...(product.variants || []).flatMap((variant) => [variant.label, variant.sku])
      ].join(' '));
      return text.includes(query);
    });

    const direction = this.querySelector('[data-aq-sort]')?.value === 'za' ? -1 : 1;
    this.filtered.sort((a, b) => direction * a.title.localeCompare(b.title, this.locale, { sensitivity: 'base', numeric: true }));
    this.render();
  }

  priceText(product) {
    if (product.pricePending || product.priceMin == null) return this.copy('pricePending');
    const value = this.money.format(product.priceMin);
    return product.priceMax != null && product.priceMax > product.priceMin ? `${this.copy('from')} ${value}` : value;
  }

  imageUrl(image) {
    if (!image) return '';
    if (typeof image === 'string') return `/cdn/shop/files/${encodeURIComponent(image)}`;
    if (image.url) return image.url;
    if (image.filename) return `/cdn/shop/files/${encodeURIComponent(image.filename)}`;
    return '';
  }

  imageCandidate(product) {
    if (product.imageUrl) return product.imageUrl;
    const explicit = (product.images || []).find((image) => image.filename === product.primaryImage);
    if (explicit) return this.imageUrl(explicit);
    if (product.images?.[0]) return this.imageUrl(product.images[0]);
    if (!product.primaryImage) return '';
    return `/cdn/shop/files/${encodeURIComponent(product.primaryImage)}`;
  }

  escape(value) {
    return String(value ?? '').replaceAll('&','&amp;').replaceAll('<','&lt;').replaceAll('>','&gt;').replaceAll('"','&quot;').replaceAll("'",'&#039;');
  }

  card(product) {
    const image = this.imageCandidate(product);
    const imageHtml = image
      ? `<img src="${this.escape(image)}" alt="${this.escape(product.title)}" loading="lazy" decoding="async" onerror="this.hidden=true;this.nextElementSibling.hidden=false">`
      : '';
    return `<article class="aq-product-card">
      <div class="aq-product-card__media">${imageHtml}<div class="aq-product-card__fallback" ${image ? 'hidden' : ''}><span>${this.escape(product.title.slice(0,2).toUpperCase())}</span></div></div>
      <div class="aq-product-card__body">
        ${product.vendor ? `<span class="aq-product-card__vendor">${this.escape(product.vendor)}</span>` : ''}
        <h3 class="aq-product-card__title">${this.escape(product.title)}</h3>
        <p class="aq-product-card__type">${this.escape(product.productType || product.collection || '')}</p>
        <div class="aq-product-card__price${product.pricePending ? ' is-pending' : ''}">${this.escape(this.priceText(product))}</div>
        <span class="aq-product-card__availability">${this.escape(this.copy('availability'))}</span>
        <button class="aq-product-card__details" type="button" data-aq-details="${this.escape(product.handle)}">${this.escape(this.copy('details'))}</button>
      </div>
    </article>`;
  }

  render() {
    const grid = this.querySelector('[data-aq-grid]');
    const status = this.querySelector('[data-aq-status]');
    const more = this.querySelector('[data-aq-more]');
    if (!grid || !status) return;
    const count = this.filtered.length;
    status.textContent = `${count} ${count === 1 ? this.copy('result') : this.copy('results')}`;
    if (!count) {
      grid.innerHTML = `<div class="aq-catalogue-empty">${this.escape(this.copy('noResults'))}</div>`;
      if (more) more.hidden = true;
      return;
    }
    grid.innerHTML = this.filtered.slice(0, this.visibleCount).map((product) => this.card(product)).join('');
    if (more) more.hidden = this.visibleCount >= count;
  }

  openDetails(handle) {
    const product = this.products.find((item) => item.handle === handle);
    const dialog = this.querySelector('[data-aq-dialog]');
    if (!product || !dialog) return;
    const variants = (product.variants || []).slice(0, 30);
    const extra = Math.max(0, (product.variants || []).length - variants.length);
    dialog.querySelector('[data-aq-dialog-title]').textContent = product.title;
    dialog.querySelector('[data-aq-dialog-meta]').textContent = [product.vendor, product.productType, product.collection].filter(Boolean).join(' · ');
    dialog.querySelector('[data-aq-dialog-price]').textContent = this.priceText(product);
    dialog.querySelector('[data-aq-dialog-description]').textContent = product.description || '';

    const media = dialog.querySelector('[data-aq-dialog-media]');
    const images = product.images || [];
    media.innerHTML = images.map((image, index) => {
      const url = this.imageUrl(image);
      if (!url) return '';
      return `<figure class="aq-dialog__image"><img src="${this.escape(url)}" alt="${this.escape(`${product.title} — ${index + 1}`)}" loading="lazy" decoding="async"></figure>`;
    }).join('');
    media.hidden = !media.innerHTML;

    const list = dialog.querySelector('[data-aq-dialog-variants]');
    list.innerHTML = variants.map((variant) => `<li>${this.escape(variant.label || variant.sku)}</li>`).join('');
    if (extra) list.insertAdjacentHTML('beforeend', `<li class="aq-dialog__more">+${extra} ${this.escape(this.copy('moreVariants'))}</li>`);
    dialog.showModal();
  }
}

if (!customElements.get('afroqueen-catalogue-browser')) customElements.define('afroqueen-catalogue-browser', AfroQueenCatalogueBrowser);
